/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  useMaterialUIController,
  setLayout,
  decrementCartItem,
  removeCartItem,
  clearCart,
  setCartItems,
} from "context";
import AppLoader from "components/AppLoader";
import CommonFooter from "components/CommonFooter";
import CommonStoreHeader from "components/CommonStoreHeader";
import { fetchAllPublicCategories } from "utils/categoryApi";
import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";

import productPlaceholder from "assets/images/product-placeholder.svg";

const toSafeNumber = (value) => {
  const parsed = typeof value === "string" ? Number(value.replace(/[^0-9.-]/g, "")) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  `Rs ${toSafeNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const toSlug = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const carouselAccents = ["#f59e0b", "#65a30d", "#f472b6", "#3b82f6", "#ef4444", "#22c55e"];

function Cart() {
  const [controller, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const { cartItems } = controller;
  const [checkoutOffers, setCheckoutOffers] = useState([]);
  const [carouselLoadError, setCarouselLoadError] = useState("");
  const [isCarouselBootLoading, setIsCarouselBootLoading] = useState(true);
  const [isCartBootLoading, setIsCartBootLoading] = useState(true);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [offerStart, setOfferStart] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const visibleOfferCount = isMobile ? 1 : isTablet ? 2 : isLaptop ? 3 : 4;

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const loadCartCarouselCategories = async () => {
      try {
        if (isMounted) setCarouselLoadError("");
        const allCategories = await fetchAllPublicCategories();
        const mainCategories = allCategories.filter((category) => category?.displayInMain);
        const mapped = mainCategories.map((category, index) => ({
          id: `main-category-${category.id || category.name}`,
          name: category?.name || "Featured Category",
          image: category?.imageUrl || productPlaceholder,
          accent: carouselAccents[index % carouselAccents.length],
          to: `/category/${toSlug(category?.name || "")}`,
        }));

        if (isMounted) {
          setCheckoutOffers(mapped);
          setOfferStart(0);
        }
      } catch (error) {
        if (isMounted) {
          setCheckoutOffers([]);
          setCarouselLoadError("We could not load featured categories right now.");
        }
      } finally {
        if (isMounted) {
          setIsCarouselBootLoading(false);
        }
      }
    };

    loadCartCarouselCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  const itemCount = useMemo(
    () => cartItems.reduce((total, item) => total + toSafeNumber(item.quantity), 0),
    [cartItems]
  );

  const subtotal = useMemo(
    () =>
      cartItems.reduce(
        (total, item) => total + toSafeNumber(item.price) * toSafeNumber(item.quantity),
        0
      ),
    [cartItems]
  );
  const totalAmount = subtotal + deliveryFee;
  const carouselOffers =
    checkoutOffers.length > 0
      ? checkoutOffers
      : [
          {
            id: "ghost-category",
            name: "Categories",
            image: productPlaceholder,
            accent: "#94a3b8",
            to: "#",
          },
        ];
  const canSlideOffers = carouselOffers.length > visibleOfferCount;
  const visibleOffers = Array.from(
    { length: Math.min(visibleOfferCount, carouselOffers.length) },
    (_, offset) => carouselOffers[(offerStart + offset) % carouselOffers.length]
  );

  const showNextOfferSet = () => {
    if (!canSlideOffers) return;
    setOfferStart((prev) => (prev + 1) % carouselOffers.length);
  };

  const showPreviousOfferSet = () => {
    if (!canSlideOffers) return;
    setOfferStart((prev) => (prev - 1 + carouselOffers.length) % carouselOffers.length);
  };

  useEffect(() => {
    if (!canSlideOffers) return undefined;

    const timer = setInterval(() => {
      setOfferStart((prev) => (prev + 1) % carouselOffers.length);
    }, 2200);

    return () => clearInterval(timer);
  }, [canSlideOffers, carouselOffers.length]);

  const handleClearAll = async () => {
    const token = localStorage.getItem("token");
    if (!token || cartItems.length === 0) {
      clearCart(dispatch);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${appSettings.baseUrl}${appSettings.endpoints.clearCart}`,
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to clear cart on server.");
      }

      clearCart(dispatch);
    } catch (error) {
      console.error("Failed to clear cart:", error);
    }
  };

  const mapCartResponseToState = (data) =>
    Array.isArray(data)
      ? data.map((serverItem) => {
          const rawImage =
            Array.isArray(serverItem.imageUrls) && serverItem.imageUrls.length > 0
              ? serverItem.imageUrls[0]
              : "";
          const image =
            rawImage && (rawImage.startsWith("http://") || rawImage.startsWith("https://"))
              ? rawImage
              : rawImage
              ? `${appSettings.baseUrl}${rawImage.startsWith("/") ? rawImage : `/${rawImage}`}`
              : "";

          return {
            id: Number(serverItem.listingId),
            listingId: Number(serverItem.listingId),
            name: serverItem.title || "",
            size: "",
            image,
            price: toSafeNumber(serverItem.price),
            quantity: Math.max(1, toSafeNumber(serverItem.quantity)),
          };
        })
      : [];

  const refreshCartFromServer = async () => {
    const response = await fetchWithAuth(
      `${appSettings.baseUrl}${appSettings.endpoints.getCartItems}`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch cart items.");
    }
    const data = await response.json();
    setCartItems(dispatch, mapCartResponseToState(data));
  };

  const refreshDeliveryFeeFromServer = async () => {
    const response = await fetchWithAuth(
      `${appSettings.baseUrl}${appSettings.endpoints.getCheckoutConfirmation}`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch checkout confirmation.");
    }
    const data = await response.json();
    setDeliveryFee(toSafeNumber(data?.deliveryFee));
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsCartBootLoading(false);
      return;
    }

    Promise.all([refreshCartFromServer(), refreshDeliveryFeeFromServer()])
      .catch((error) => {
        console.error("Failed to refresh cart data from server on load:", error);
      })
      .finally(() => {
        setIsCartBootLoading(false);
      });
  }, []);

  const isPageBootLoading = isCarouselBootLoading || isCartBootLoading;

  const handleIncrementItem = async (item) => {
    const listingId = Number(item?.listingId ?? item?.id ?? item?.productId);
    const token = localStorage.getItem("token");

    if (!token) {
      incrementCartItem(dispatch, item.id);
      return;
    }

    if (!Number.isFinite(listingId) || listingId <= 0) return;

    try {
      const response = await fetchWithAuth(
        `${appSettings.baseUrl}${appSettings.endpoints.addMemberListing}`,
        {
          method: "POST",
          body: JSON.stringify({ listingId, status: "Cart" }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to increment cart item.");
      }

      await refreshCartFromServer();
    } catch (error) {
      console.error("Failed to increment cart item:", error);
    }
  };

  const handleDecrementItem = async (item) => {
    const listingId = Number(item?.listingId ?? item?.id ?? item?.productId);
    const token = localStorage.getItem("token");

    if (!token) {
      decrementCartItem(dispatch, item.id);
      return;
    }

    if (!Number.isFinite(listingId) || listingId <= 0) return;

    try {
      const response = await fetchWithAuth(
        `${appSettings.baseUrl}${appSettings.endpoints.decrementCartItem}`,
        {
          method: "POST",
          body: JSON.stringify({ listingId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to decrement cart item.");
      }

      await refreshCartFromServer();
    } catch (error) {
      console.error("Failed to decrement cart item:", error);
    }
  };

  const handleRemoveItem = async (item) => {
    const itemId = item?.id;
    const listingId = Number(item?.listingId ?? item?.id ?? item?.productId);
    const token = localStorage.getItem("token");
    if (!token) {
      removeCartItem(dispatch, itemId);
      return;
    }

    if (!Number.isFinite(listingId) || listingId <= 0) {
      console.error("Invalid listing id for remove-cart-item request:", item);
      removeCartItem(dispatch, itemId);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${appSettings.baseUrl}${appSettings.endpoints.removeCartItem}`,
        {
          method: "POST",
          body: JSON.stringify({ listingId }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to remove cart item on server.");
      }

      removeCartItem(dispatch, itemId);
    } catch (error) {
      console.error("Failed to remove cart item:", error);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4f6" }}>
      <CommonStoreHeader />

      {isPageBootLoading ? (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            bgcolor: "rgba(243, 244, 246, 0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AppLoader size={40} color="#b71c2a" />
        </Box>
      ) : (
        <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: 3 }}>
          <Box
            sx={{
              p: { xs: 1.6, md: 2 },
              borderRadius: 2.2,
              border: "1px solid #fecdd3",
              bgcolor: "#ffffff",
              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
              mb: 2.2,
              background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 70%)",
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ color: "#64748b", fontWeight: 700, fontSize: "0.85rem" }}>
                  READY TO CHECKOUT
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: "1.3rem", md: "1.75rem" },
                    mt: 0.3,
                    color: "#7f1d1d",
                  }}
                >
                  Review Cart ({itemCount} items)
                </Typography>
              </Box>
              <Button
                variant="text"
                onClick={handleClearAll}
                sx={{
                  color: "#b71c2a",
                  fontWeight: 800,
                  minWidth: "fit-content",
                  "&:hover": { bgcolor: "#fff1f2" },
                }}
              >
                Clear All
              </Button>
            </Stack>
          </Box>

          {cartItems.length === 0 ? (
            <Card
              sx={{
                p: { xs: 2.4, md: 4 },
                borderRadius: 2.6,
                border: "1px solid #fecdd3",
                textAlign: "center",
                boxShadow: "0 14px 28px rgba(15, 23, 42, 0.08)",
                background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 58%)",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  mb: 0.7,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#7f1d1d",
                  letterSpacing: "0.01em",
                }}
              >
                Your cart is empty
              </Typography>
              <Typography
                sx={{ color: "#475569", mb: 3.1, fontSize: { xs: "1.02rem", md: "1.28rem" } }}
              >
                Add items from categories to see them here.
              </Typography>
              <Button
                component={Link}
                to="/"
                variant="contained"
                sx={{
                  px: { xs: 3.4, md: 4.6 },
                  py: 1.2,
                  borderRadius: 1.6,
                  bgcolor: "#b71c2a",
                  color: "#ffffff",
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                  boxShadow: "0 12px 22px rgba(183, 28, 42, 0.28)",
                  "&:hover": { bgcolor: "#991b1b" },
                }}
              >
                Continue Shopping
              </Button>
            </Card>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(280px, 1fr)" },
                gap: 2,
                alignItems: "start",
              }}
            >
              <Card
                sx={{
                  borderRadius: 2.2,
                  p: { xs: 1.5, md: 2 },
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.06)",
                  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
                }}
              >
                {cartItems.map((item, index) => (
                  <Box key={item.id}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      sx={{
                        py: 1.35,
                        px: 1.1,
                        borderRadius: 1.7,
                        bgcolor: "#ffffff",
                        border: "1px solid #eef2f7",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          component="img"
                          src={item.image || productPlaceholder}
                          alt={item.name}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = productPlaceholder;
                          }}
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: "cover",
                            borderRadius: 1.7,
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 8px 16px rgba(15, 23, 42, 0.06)",
                          }}
                        />
                        <Box>
                          <Typography
                            sx={{ fontWeight: 900, fontSize: "1.05rem", color: "#0f172a" }}
                          >
                            {item.name}
                          </Typography>
                          {item.size &&
                            item.size.trim() !== "0 pcs" &&
                            item.size.trim() !== "0" && (
                              <Typography sx={{ color: "#64748b", fontSize: "0.88rem" }}>
                                {item.size}
                              </Typography>
                            )}
                          <Typography
                            sx={{ color: "#16a34a", fontWeight: 800, mt: 0.7, fontSize: "1.05rem" }}
                          >
                            {formatCurrency(item.price)}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" alignItems="center" spacing={1.1}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.8}
                          sx={{
                            bgcolor: "#ffffff",
                            border: "1px solid #e2e8f0",
                            borderRadius: 1.5,
                            px: 0.6,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleDecrementItem(item)}
                            sx={{ color: "#b71c2a" }}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography sx={{ minWidth: 18, textAlign: "center", fontWeight: 800 }}>
                            {item.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleIncrementItem(item)}
                            sx={{ color: "#15803d" }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => handleRemoveItem(item)}
                          sx={{
                            color: "#b71c2a",
                            fontWeight: 800,
                            minWidth: "fit-content",
                            "&:hover": { bgcolor: "#fff1f2" },
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                    </Stack>
                    {index !== cartItems.length - 1 && <Divider sx={{ my: 1.2 }} />}
                  </Box>
                ))}
              </Card>

              <Card
                sx={{
                  borderRadius: 2.2,
                  border: "1px solid #fecdd3",
                  p: 2,
                  boxShadow: "0 14px 30px rgba(183, 28, 42, 0.12)",
                  position: { lg: "sticky" },
                  top: { lg: 132 },
                  background: "linear-gradient(180deg, #fff1f2 0%, #ffffff 100%)",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: "1.24rem", color: "#7f1d1d" }}>
                  Order Summary
                </Typography>
                <Divider sx={{ my: 1.4, borderColor: "#fecdd3" }} />

                <Stack spacing={1.1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#64748b", fontWeight: 600 }}>
                      Items ({itemCount})
                    </Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatCurrency(subtotal)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ color: "#64748b", fontWeight: 600 }}>Delivery</Typography>
                    <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                      {formatCurrency(deliveryFee)}
                    </Typography>
                  </Stack>
                </Stack>

                <Divider sx={{ my: 1.5, borderColor: "#fecdd3" }} />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontWeight: 800, fontSize: "1.12rem" }}>Total</Typography>
                  <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#7f1d1d" }}>
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Stack>
                <Typography sx={{ mt: 0.6, color: "#64748b", fontSize: "0.8rem" }}>
                  Taxes included. Final total at checkout.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate("/checkout/confirmation")}
                  sx={{
                    mt: 2,
                    py: 1.2,
                    borderRadius: 1.5,
                    fontWeight: 800,
                    bgcolor: "#b71c2a",
                    color: "#ffffff !important",
                    boxShadow: "0 12px 22px rgba(183, 28, 42, 0.28)",
                    "&:hover": { bgcolor: "#991b1b" },
                  }}
                >
                  Proceed to Checkout
                </Button>
              </Card>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography
              sx={{ fontSize: { xs: "1.4rem", md: "2rem" }, fontWeight: 800, color: "#0f172a" }}
            >
              Before You Checkout
            </Typography>
            {carouselLoadError ? (
              <Card
                sx={{
                  mt: 1.4,
                  borderRadius: 2.2,
                  border: "1px solid #fecaca",
                  boxShadow: "0 10px 24px rgba(127, 29, 29, 0.08)",
                }}
              >
                <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                  <Typography sx={{ fontWeight: 800, color: "#7f1d1d", fontSize: "1rem" }}>
                    Featured categories are unavailable
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#475569" }}>
                    {carouselLoadError} Please refresh and try again.
                  </Typography>
                </Box>
              </Card>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  mt: 1.4,
                }}
              >
                <IconButton
                  aria-label="Previous offers"
                  onClick={showPreviousOfferSet}
                  disabled={!canSlideOffers}
                  sx={{
                    position: "absolute",
                    left: { xs: -8, md: -16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    bgcolor: "rgba(255,255,255,0.95)",
                    boxShadow: "0 10px 22px rgba(2, 6, 23, 0.15)",
                    "&:hover": { bgcolor: "white" },
                    "&.Mui-disabled": { opacity: 0.35 },
                  }}
                >
                  <ChevronLeftIcon />
                </IconButton>

                <Stack direction="row" spacing={2} sx={{ overflow: "hidden" }}>
                  {visibleOffers.map((offer) => (
                    <Box key={offer.id} sx={{ flex: 1, minWidth: 0 }}>
                      <Card
                        component={Link}
                        to={offer.to || "#"}
                        sx={{
                          position: "relative",
                          height: { xs: 170, md: 195 },
                          borderRadius: 2.2,
                          border: "1px solid #d1d5db",
                          overflow: "hidden",
                          textDecoration: "none",
                        }}
                      >
                        <Box
                          component="img"
                          src={offer.image}
                          alt={offer.name}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            px: { xs: 1.5, md: 2 },
                            py: { xs: 1.3, md: 1.8 },
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#111827",
                              fontWeight: 900,
                              fontSize: "2rem",
                              lineHeight: 1,
                            }}
                          >
                            {offer.name}
                          </Typography>
                          <Typography
                            sx={{ color: "#ffffff", fontWeight: 700, fontSize: "2rem", mt: 0.4 }}
                          >
                            Explore items
                          </Typography>
                        </Box>
                      </Card>
                    </Box>
                  ))}
                </Stack>

                <IconButton
                  aria-label="Next offers"
                  onClick={showNextOfferSet}
                  disabled={!canSlideOffers}
                  sx={{
                    position: "absolute",
                    right: { xs: -8, md: -16 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    bgcolor: "rgba(255,255,255,0.95)",
                    boxShadow: "0 10px 22px rgba(2, 6, 23, 0.15)",
                    "&:hover": { bgcolor: "white" },
                    "&.Mui-disabled": { opacity: 0.35 },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      )}

      <CommonFooter />
    </Box>
  );
}

export default Cart;
